import { Controller } from 'stimulus'

// Drives one kind-card on Settings -> Field Names: the All names /
// Suggestions tabs, name selection (kept in a key -> display Map so it
// survives server-side search swaps on capped accounts), the
// suggested-merge review / dismiss shortcuts, and the merge modal
// (canonical-spelling input + live impact preview, then a plain form POST
// so the redirect + flash come back like any settings action).
export default class extends Controller {
  static targets = ['box', 'row', 'tbody', 'mergeBtn', 'modal', 'targetInput', 'impact', 'summary',
    'filter', 'tabBtn', 'tabPanel', 'suggestion', 'suggestionCount', 'suggestionsEmpty'];

  connect() {
    this.chosen = new Map()
    if (this.capped && this.hasTbodyTarget) this.defaultRows = this.tbodyTarget.innerHTML
    this.syncButton()
  }

  get capped() {
    return this.data.get('capped') === 'true'
  }

  switchTab(ev) {
    const { tab } = ev.currentTarget.dataset
    this.tabBtnTargets.forEach((btn) => {
      const active = btn.dataset.tab === tab
      btn.classList.toggle('border-blue-500', active)
      btn.classList.toggle('text-blue-600', active)
      btn.classList.toggle('border-transparent', !active)
      btn.classList.toggle('text-gray-500', !active)
    })
    this.tabPanelTargets.forEach((panel) => {
      panel.classList.toggle('hidden', panel.dataset.tab !== tab)
    })
  }

  filter() {
    if (this.capped) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.serverFilter(), 300)
      return
    }
    const query = this.filterTarget.value.trim().toLowerCase()
    this.rowTargets.forEach((row) => {
      const el = row
      el.style.display = !query || el.dataset.key.includes(query) ? '' : 'none'
    })
  }

  // The rendered list only holds the most-used names on capped accounts, so
  // the filter queries the server (which searches ALL names) and swaps the
  // table body. An empty query restores the initially rendered rows.
  serverFilter() {
    const query = this.filterTarget.value.trim()
    if (!query) {
      this.tbodyTarget.innerHTML = this.defaultRows
      this.reapplyChecks()
      return
    }
    const params = new URLSearchParams({ kind: this.data.get('kind'), q: query })
    fetch(`${this.data.get('searchUrl')}?${params}`, { headers: { Accept: 'text/html' } })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then((html) => {
        if (query !== this.filterTarget.value.trim()) return // stale response
        this.tbodyTarget.innerHTML = html
        this.reapplyChecks()
      })
      .catch(() => {})
  }

  reapplyChecks() {
    this.boxTargets.forEach((box) => {
      const el = box
      el.checked = this.chosen.has(el.dataset.key)
    })
  }

  toggleBox(ev) {
    const { key, display } = ev.currentTarget.dataset
    if (ev.currentTarget.checked) this.chosen.set(key, display)
    else this.chosen.delete(key)
    this.syncButton()
  }

  syncButton() {
    if (!this.hasMergeBtnTarget) return
    const count = this.chosen.size
    this.mergeBtnTarget.disabled = count === 0
    if (count === 0) this.mergeBtnTarget.textContent = 'Merge selected…'
    else this.mergeBtnTarget.textContent = count === 1 ? 'Rename 1 name…' : `Merge ${count} names…`
  }

  suggest(ev) {
    const keys = JSON.parse(ev.currentTarget.dataset.keys)
    const displays = JSON.parse(ev.currentTarget.dataset.displays)
    this.chosen = new Map(keys.map((key, i) => [key, displays[i] || key]))
    this.reapplyChecks()
    this.syncButton()
    this.openMerge()
  }

  dismiss(ev) {
    const btn = ev.currentTarget
    const suggestion = btn.closest('[data-keys]')
    if (!suggestion) return
    btn.disabled = true
    const body = new URLSearchParams({ kind: this.data.get('kind') })
    JSON.parse(suggestion.dataset.keys).forEach(key => body.append('keys[]', key))
    fetch(this.data.get('dismissUrl'), {
      method: 'POST',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
      body
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then(() => {
        suggestion.remove()
        this.syncSuggestionCount()
      })
      .catch(() => { btn.disabled = false })
  }

  syncSuggestionCount() {
    const count = this.suggestionTargets.length
    if (this.hasSuggestionCountTarget) this.suggestionCountTarget.textContent = count
    if (this.hasSuggestionsEmptyTarget) this.suggestionsEmptyTarget.classList.toggle('hidden', count > 0)
  }

  openMerge() {
    if (!this.chosen.size) return
    const displays = [...this.chosen.values()]
    const [first] = displays
    this.targetInputTarget.value = first
    this.summaryTarget.textContent = displays.join('  +  ')
    this.modalTarget.classList.remove('hidden')
    this.previewImpact()
  }

  closeMerge() {
    this.modalTarget.classList.add('hidden')
  }

  previewImpact() {
    const target = this.targetInputTarget.value.trim()
    if (!target) {
      this.impactTarget.textContent = 'Enter a new spelling to see the impact.'
      return
    }
    this.impactTarget.textContent = 'Calculating impact…'
    const params = new URLSearchParams({ kind: this.data.get('kind'), target })
    this.chosen.forEach((_display, key) => params.append('keys[]', key))
    fetch(`${this.data.get('previewUrl')}?${params}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((impact) => {
        this.impactTarget.textContent = impact.message
      })
      .catch(() => {
        this.impactTarget.textContent = 'Could not calculate the impact preview.'
      })
  }

  submit() {
    const target = this.targetInputTarget.value.trim()
    if (!target || !this.chosen.size) return
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.data.get('mergeUrl')
    const add = (name, value) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }
    add('authenticity_token', Rails.csrfToken())
    add('kind', this.data.get('kind'))
    add('target', target)
    this.chosen.forEach((_display, key) => add('keys[]', key))
    document.body.appendChild(form)
    form.submit()
  }
}
